import React from 'react';

import type { CodeBlockSuggestionError } from '@aline/common';
import { IconButton, Typography } from '@mui/material';
import { ChevronDownIcon } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

import { useVisibilityButtonStyles, VisibilityButton } from './VisibilityButton';
import { SuggestionStatus } from '../../../../chat-types';
import type { SuggestionAndStatus } from '../../../ChatPanelMarkdown';

const useStyles = makeStyles()(theme => ({
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
  },
  headerRight: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(0.25),
    marginLeft: 'auto',
  },
}));

export const SuggestionItemHeaderError = React.memo(function SuggestionItemHeaderError({
  error,
  handleToggleExpand,
}: {
  error: CodeBlockSuggestionError;
  handleToggleExpand: (e: React.MouseEvent) => void;
}) {
  const { classes } = useStyles();
  const { classes: visibilityButtonClasses } = useVisibilityButtonStyles();

  return (
    <div className={classes.header}>
      <Typography variant="subtitle2">{error.title}</Typography>
      <div className={classes.headerRight}>
        <SuggestionStatusChip status="error" />
        <IconButton size="small" onClick={handleToggleExpand}>
          <ChevronDownIcon className={visibilityButtonClasses.headerIcon} />
        </IconButton>
      </div>
    </div>
  );
});

export const SuggestionItemHeader = React.memo(function SuggestionItemHeader({
  isSuggestionInDocument,
  isSuggestionVisible,
  suggestionAndStatus,
}: {
  isSuggestionInDocument: boolean;
  isSuggestionVisible: boolean;
  suggestionAndStatus: SuggestionAndStatus;
}) {
  const { classes } = useStyles();
  const { status: suggestionStatus, suggestion } = suggestionAndStatus;

  return (
    <div className={classes.header}>
      <Typography variant="subtitle2">{suggestion.title}</Typography>
      <div className={classes.headerRight}>
        {getIsSuggestionStatusChipStatus(suggestionStatus) && !isSuggestionInDocument && (
          <SuggestionStatusChip status={suggestionStatus} />
        )}
        <VisibilityButton isSuggestionVisible={isSuggestionVisible} suggestion={suggestion} />
      </div>
    </div>
  );
});

const useChipStyles = makeStyles<SuggestionStatusChipProps>()((theme, { status }) => ({
  chip: {
    alignItems: 'center',
    background: getSuggestionStatusChipColors(status).chipColor,
    borderRadius: 16,
    boxSizing: 'border-box',
    display: 'flex',
    height: 20,
    padding: theme.spacing(0, 1),
    width: 'fit-content',
  },
  chipText: {
    color: getSuggestionStatusChipColors(status).textColor,
    fontSize: 10,
    fontWeight: 600,
  },
}));

type SuggestionStatusChipStatus =
  | typeof SuggestionStatus.Accepted
  | typeof SuggestionStatus.Redlined
  | typeof SuggestionStatus.Rejected
  | typeof SuggestionStatus.TextChanged
  | 'error';

interface SuggestionStatusChipProps {
  status: SuggestionStatusChipStatus | 'error';
}

export function SuggestionStatusChip({ status }: SuggestionStatusChipProps): JSX.Element {
  const { classes } = useChipStyles({ status });

  function getStatusText(status: SuggestionStatusChipStatus): string {
    switch (status) {
      case SuggestionStatus.Accepted:
        return 'Accepted';
      case SuggestionStatus.TextChanged:
        return 'Stale';
      case SuggestionStatus.Redlined:
        return 'Redlined';
      case SuggestionStatus.Rejected:
        return 'Rejected';
      case 'error':
        return 'Error';
    }
  }

  return (
    <div className={classes.chip}>
      <Typography noWrap className={classes.chipText}>
        {getStatusText(status)}
      </Typography>
    </div>
  );
}

function getIsSuggestionStatusChipStatus(
  status: SuggestionStatus
): status is Exclude<SuggestionStatusChipStatus, 'error'> {
  switch (status) {
    case SuggestionStatus.Accepted:
    case SuggestionStatus.Redlined:
    case SuggestionStatus.Rejected:
    case SuggestionStatus.TextChanged:
      return true;
    default:
      return false;
  }
}

function getSuggestionStatusChipColors(status: SuggestionStatusChipStatus): {
  chipColor: string;
  textColor: string;
} {
  switch (status) {
    case SuggestionStatus.Accepted:
      return { chipColor: '#D1FADF', textColor: '#039855' };
    case SuggestionStatus.TextChanged:
      return { chipColor: '#FEF0C7', textColor: '#DC6803' };
    case SuggestionStatus.Redlined:
      return { chipColor: '#E0F2FE', textColor: '#0086C9' };
    case SuggestionStatus.Rejected:
    case 'error':
      return { chipColor: '#FEE4E2', textColor: '#D91F11' };
  }
}
